import type { FaqItem, Guide, Hub, JsonLdNode, Post, PostSummary, SourceItem } from '@/types'

import { siteConfig } from '@/constants/site'

const ORG_ID = `${siteConfig.url}/#organization`
const WEBSITE_ID = `${siteConfig.url}/#website`

const absolute = (path: string): string =>
    path.startsWith('http') ? path : `${siteConfig.url}${path.startsWith('/') ? '' : '/'}${path}`

// Sitewide publisher entity. Health content is YMYL, so we make the
// publishing organization an explicit, referenceable node for E-E-A-T.
const organizationNode = (): JsonLdNode => ({
    '@type': 'Organization',
    '@id': ORG_ID,
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    logo: {
        '@type': 'ImageObject',
        url: absolute('/logo.svg')
    },
    image: absolute(siteConfig.ogImage),
    email: siteConfig.email,
    // The public statement of how this library is sourced, dated and corrected.
    // It is the one E-E-A-T signal here that is fully checkable, so it is wired
    // into the publisher entity rather than left as a footer link.
    publishingPrinciples: absolute('/methodology')
})

const websiteNode = (): JsonLdNode => ({
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: siteConfig.url,
    name: siteConfig.name,
    description: siteConfig.description,
    inLanguage: 'en-US',
    publisher: { '@id': ORG_ID },
    potentialAction: {
        '@type': 'SearchAction',
        target: {
            '@type': 'EntryPoint',
            urlTemplate: `${siteConfig.url}/search?q={search_term_string}`
        },
        'query-input': 'required name=search_term_string'
    }
})

// Every page ships a self-contained graph: the publisher + the site, plus
// whatever page-specific nodes are passed in. Shared @ids let crawlers and
// answer engines stitch the entities together.
export const buildGraph = (nodes: JsonLdNode[] = []): JsonLdNode => ({
    '@context': 'https://schema.org',
    '@graph': [organizationNode(), websiteNode(), ...nodes]
})

export const breadcrumbNode = (items: { name: string; path: string }[]): JsonLdNode => ({
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: absolute(item.path)
    }))
})

export const collectionNode = (hub: Hub, posts: PostSummary[]): JsonLdNode => ({
    '@type': 'CollectionPage',
    '@id': `${absolute(`/${hub.slug}`)}#collection`,
    url: absolute(`/${hub.slug}`),
    name: `${hub.name} | ${siteConfig.name}`,
    description: hub.description,
    inLanguage: 'en-US',
    isPartOf: { '@id': WEBSITE_ID },
    publisher: { '@id': ORG_ID },
    mainEntity: {
        '@type': 'ItemList',
        itemListElement: posts.map((post, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            url: absolute(`/${post.hub}/${post.slug}`),
            name: post.title
        }))
    }
})

export const aboutNode = (): JsonLdNode => ({
    '@type': 'AboutPage',
    '@id': `${absolute('/about')}#about`,
    url: absolute('/about'),
    name: `About | ${siteConfig.name}`,
    description:
        'HealthPathCore is an independent, evidence-informed health knowledge library. Learn about our mission, the topics we cover, and our editorial standards.',
    inLanguage: 'en-US',
    isPartOf: { '@id': WEBSITE_ID },
    about: { '@id': ORG_ID }
})

// Articles are authored by the organization itself. We deliberately make no
// claim about a named person or a clinical reviewer, because an unverifiable
// credential claim is the thing that actually costs trust in a YMYL category.
export const articleNode = (post: Post, hubName: string): JsonLdNode => {
    const url = absolute(`/${post.hub}/${post.slug}`)
    const image = post.coverImage ? absolute(post.coverImage) : absolute(siteConfig.ogImage)

    const node: JsonLdNode = {
        '@type': 'Article',
        '@id': `${url}#article`,
        url,
        headline: post.title,
        name: post.title,
        description: post.excerpt,
        image: [image],
        inLanguage: 'en-US',
        datePublished: post.publishedAt,
        dateModified: post.updatedAt || post.publishedAt,
        isAccessibleForFree: true,
        articleSection: hubName,
        wordCount: post.content ? post.content.replace(/<[^>]+>/g, ' ').split(/\s+/).filter(Boolean).length : undefined,
        timeRequired: `PT${post.readMinutes}M`,
        author: { '@id': ORG_ID },
        publisher: { '@id': ORG_ID },
        isPartOf: post.guideSlug
            ? [{ '@id': WEBSITE_ID }, { '@id': guideId(post.guideSlug) }]
            : { '@id': WEBSITE_ID },
        mainEntityOfPage: { '@type': 'WebPage', '@id': url }
    }

    if (post.tags.length > 0) node.keywords = post.tags.join(', ')

    const citations = citationList(post.sources)
    if (citations) node.citation = citations

    return node
}

const guideId = (slug: string): string => `${absolute(`/guides/${slug}`)}#guide`

const citationList = (sources: SourceItem[]): JsonLdNode[] | undefined => {
    if (!sources || sources.length === 0) return undefined

    return sources.map(source => ({
        '@type': 'CreativeWork',
        name: source.label,
        publisher: source.publisher,
        url: source.url
    }))
}

// A pillar guide is the parent of its topic cluster: hasPart names every
// article beneath it, and each of those articles points back via isPartOf.
export const guideNode = (guide: Guide, hubName: string, chapters: PostSummary[]): JsonLdNode => {
    const url = absolute(`/guides/${guide.slug}`)
    const image = guide.coverImage ? absolute(guide.coverImage) : absolute(siteConfig.ogImage)

    const node: JsonLdNode = {
        '@type': 'Article',
        '@id': guideId(guide.slug),
        url,
        headline: guide.title,
        name: guide.title,
        description: guide.standfirst,
        image: [image],
        inLanguage: 'en-US',
        datePublished: guide.publishedAt,
        dateModified: guide.updatedAt || guide.publishedAt,
        isAccessibleForFree: true,
        articleSection: hubName,
        timeRequired: `PT${guide.readMinutes}M`,
        author: { '@id': ORG_ID },
        publisher: { '@id': ORG_ID },
        isPartOf: { '@id': WEBSITE_ID },
        mainEntityOfPage: { '@type': 'WebPage', '@id': url }
    }

    if (chapters.length > 0) {
        node.hasPart = chapters.map(chapter => ({
            '@type': 'Article',
            '@id': `${absolute(`/${chapter.hub}/${chapter.slug}`)}#article`,
            url: absolute(`/${chapter.hub}/${chapter.slug}`),
            name: chapter.title,
            headline: chapter.title
        }))
    }

    const citations = citationList(guide.sources)
    if (citations) node.citation = citations

    return node
}

// Emitted ONLY when a page actually carries FAQ entries. An empty FAQPage is a
// manual action in Search Console, so never render this node speculatively.
export const faqNode = (items: FaqItem[], path: string): JsonLdNode | null => {
    if (!items || items.length === 0) return null

    return {
        '@type': 'FAQPage',
        '@id': `${absolute(path)}#faq`,
        mainEntity: items.map(item => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer
            }
        }))
    }
}

export const methodologyNode = (): JsonLdNode => ({
    '@type': 'WebPage',
    '@id': `${absolute('/methodology')}#methodology`,
    url: absolute('/methodology'),
    name: `How we research and write | ${siteConfig.name}`,
    description:
        'How HealthPathCore researches, writes, dates and corrects its health guides: which sources are treated as authoritative, what the site will not claim, and how to report an error.',
    inLanguage: 'en-US',
    isPartOf: { '@id': WEBSITE_ID },
    about: { '@id': ORG_ID }
})

export const guidesIndexNode = (guides: Guide[] | { slug: string; title: string }[]): JsonLdNode => ({
    '@type': 'CollectionPage',
    '@id': `${absolute('/guides')}#collection`,
    url: absolute('/guides'),
    name: `Complete guides | ${siteConfig.name}`,
    description:
        'In-depth guides to mental health, physical health, and nutrition, each linking to the full set of articles on that topic.',
    inLanguage: 'en-US',
    isPartOf: { '@id': WEBSITE_ID },
    publisher: { '@id': ORG_ID },
    mainEntity: {
        '@type': 'ItemList',
        itemListElement: guides.map((guide, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            url: absolute(`/guides/${guide.slug}`),
            name: guide.title
        }))
    }
})
